import React, { useState, useEffect } from 'react';
import { fetchIpWhitelist, addIpToWhitelist, removeIpFromWhitelist } from '../api';
import { useApp } from '../contexts/AppContext';
import { t } from '../utils/translations';

const IpWhitelist: React.FC = () => {
  const [ips, setIps] = useState<string[]>([]);
  const [newIp, setNewIp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { language } = useApp();
  
  // Handle IP address input filtering
  const handleIpInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow numbers, dots, and colons for IPv4 and IPv6
    let filteredValue = value.replace(/[^0-9.:a-fA-F]/g, '');
    setNewIp(filteredValue);
  };
  
  // Validate IP address format
  const isValidIp = (ip: string): boolean => {
    // Basic IPv4 pattern
    const ipv4Pattern = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
    // Basic IPv6 pattern (simplified)
    const ipv6Pattern = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    return ipv4Pattern.test(ip) || ipv6Pattern.test(ip);
  };
  
  // Load IP list
  const loadIps = async () => {
    try {
      setLoading(true);
      setError(null);
      const ipList = await fetchIpWhitelist();
      setIps(ipList);
    } catch (err) {
      setError(t('loadIpListFailed', language));
      console.error('Failed to load IP whitelist:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Add IP
  const handleAddIp = async () => {
    if (!newIp.trim()) {
      setError(t('pleaseEnterNameAndIp', language));
      return;
    }
    
    if (!isValidIp(newIp.trim())) {
      setError(t('invalidIpFormat', language));
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      const result = await addIpToWhitelist(newIp.trim());
      console.log('IP add result:', result);
      
      // Clear input
      setNewIp('');
      
      // Show appropriate message based on HTTP status
      if ((result as any).__httpStatus === 200) {
        // IP already existed
        setSuccessMessage(t('ipAlreadyExists', language, { ip: newIp.trim() }));
      } else {
        // IP was newly added
        setSuccessMessage(result.message);
      }
      
      // Always reload the IP list to ensure consistency
      await loadIps();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to add IP:', err);
      // Check if it's a specific error response
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as any;
        if (axiosError.response?.data?.message) {
          setError(axiosError.response.data.message);
        } else {
          setError(t('addIpFailed', language));
        }
      } else {
        setError(t('addIpFailed', language));
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Delete IP
  const handleDeleteIp = async (ipAddress: string) => {
    const confirmMessage = t('deleteIp', language, { ip: ipAddress });
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      const result = await removeIpFromWhitelist(ipAddress);
      console.log('IP removed successfully:', result);
      
      setSuccessMessage(result.message);
      await loadIps();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(t('deleteIpFailed', language));
      console.error('Failed to delete IP:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddIp();
    }
  };
  
  // Initial load
  useEffect(() => {
    loadIps();
  }, []);
  
  return (
    <div className="space-y-6 md:space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
          {t('ipWhitelistTitle', language)}
        </h2>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6" role="alert">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg mb-6" role="alert">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{successMessage}</span>
            </div>
          </div>
        )}
        
        {/* Add IP Section */}
        <div className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 p-4 md:p-6 rounded-xl mb-6">
          <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-gray-100">
            {t('addNewIp', language)}
          </h3>
          <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-4">
            <div>
              <label htmlFor="ip" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('ipAddress', language)}
              </label>
              <input
                type="text"
                id="ip"
                placeholder={t('ipPlaceholder', language)}
                value={newIp}
                onChange={handleIpInputChange}
                onKeyDown={handleKeyPress}
                className="block w-full px-3 py-2 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>
            
            <div className="md:flex md:items-end">
              <button
                onClick={handleAddIp}
                disabled={loading || !newIp.trim()}
                className="w-full inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-150"
                style={{ minHeight: '44px' }}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {loading ? t('loading', language) : t('add', language)}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* IP List Section */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <div className="w-6 h-6 rounded-md bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            {t('currentIpWhitelist', language)}
          </h3>
          <button
            onClick={loadIps}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-150"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="inline">{t('refresh', language)}</span>
          </button>
        </div>
        
        {/* Desktop Table */}
        <div className="hidden md:block bg-white dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('ipAddress', language)}
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('operation', language)}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-700/50 divide-y divide-gray-200 dark:divide-gray-600">
                {ips.length > 0 ? (
                  ips.map((ipAddress) => (
                    <tr key={ipAddress} className="hover:bg-gray-50 dark:hover:bg-gray-600/50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 font-mono">
                        {ipAddress}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => handleDeleteIp(ipAddress)}
                          disabled={loading}
                          className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors duration-150 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          {t('delete', language)}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        {t('noIpRecords', language)}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card Layout */}
        <div className="md:hidden space-y-3">
          {ips.length === 0 ? (
            <div className="bg-white dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600 p-8 text-center">
              <svg className="w-12 h-12 text-gray-400 mb-3 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('noIpRecords', language)}</p>
            </div>
          ) : (
            ips.map((ipAddress) => (
              <div key={ipAddress} className="bg-white dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600 p-4">
                <div className="flex items-center justify-between">
                  <div className="font-mono font-medium text-lg text-gray-900 dark:text-gray-100">{ipAddress}</div>
                  <button
                    onClick={() => handleDeleteIp(ipAddress)}
                    disabled={loading}
                    className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors duration-150 disabled:opacity-50 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {t('delete', language)}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default IpWhitelist; 